import React, { memo } from 'react';
import { Box, Button, FormControlLabel, IconButton, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import type { IdCardDesignInput } from '../../../types/idCards';
import { INSTRUCTION_MAX_LENGTH, MAX_INSTRUCTIONS } from '../catalog';
import type { CardSchool } from '../render/faces/shared';

type Back = IdCardDesignInput['back'];

interface Props {
  value: Back;
  onChange: (back: Back) => void;
  school: CardSchool | null;
}

/** The back of the card: the return address, the school's rules and who signs. */
const BackSideSettings: React.FC<Props> = ({ value, onChange, school }) => {
  const set = (patch: Partial<Back>) => onChange({ ...value, ...patch });
  const setLine = (i: number, text: string) => set({ instructions: value.instructions.map((l, j) => (j === i ? text : l)) });
  const contact = [school?.address, school?.phone].filter(Boolean).join(' · ');

  return (
    <Stack spacing={1.5}>
      <FormControlLabel
        control={<Switch checked={value.enabled} onChange={e => set({ enabled: e.target.checked })} />}
        label={<Typography variant="body2" fontWeight={600}>Print a back</Typography>}
      />
      {value.enabled && (
        <Stack spacing={2} sx={{ pl: 0.5 }}>
          <Box>
            <FormControlLabel
              control={<Switch size="small" checked={value.showSchoolContact} onChange={e => set({ showSchoolContact: e.target.checked })} />}
              label={<Typography variant="body2">“If found, please return to” the school</Typography>}
            />
            {value.showSchoolContact && (
              <Typography variant="caption" color={contact ? 'text.secondary' : 'warning.main'} sx={{ display: 'block', pl: 5.5, mt: -0.25 }}>
                {contact || 'The school has no address or phone on record. Add them in Settings → School configuration.'}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Instructions</Typography>
            <Stack spacing={1}>
              {value.instructions.map((line, i) => (
                <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    maxRows={3}
                    value={line}
                    onChange={e => setLine(i, e.target.value.slice(0, INSTRUCTION_MAX_LENGTH))}
                    placeholder="e.g. This card must be shown at the gate."
                    helperText={line.length > INSTRUCTION_MAX_LENGTH - 20 ? `${line.length}/${INSTRUCTION_MAX_LENGTH}` : undefined}
                  />
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => set({ instructions: value.instructions.filter((_, j) => j !== i) })} sx={{ mt: 0.5 }} aria-label="Remove instruction">
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
              {value.instructions.length < MAX_INSTRUCTIONS && (
                <Button size="small" startIcon={<Add />} onClick={() => set({ instructions: [...value.instructions, ''] })} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                  Add instruction
                </Button>
              )}
            </Stack>
          </Box>

          <TextField
            size="small"
            label="Signed by"
            value={value.signatory}
            onChange={e => set({ signatory: e.target.value.slice(0, 40) })}
            helperText="The title under the signature line, e.g. Principal."
          />
        </Stack>
      )}
    </Stack>
  );
};

export default memo(BackSideSettings);
